import { IkeCloneable, IkeFieldType, type SubFormAnchorTypeState, SubFormType } from '@ikegps/core-api';
import { IkeId } from '../../../../id';
import { IkePoleForemanFieldIds, PoleFieldIds } from '../../../pole/integrations';
import type { FormModelState } from '../../form.state';
import type { FormFieldSubFormState, FormModelFieldSubForm } from '../form.subform';
import { LOCKED_MODEL_POLE_FIELDS } from '../../form.field.lock';
import { isValidLeadLengthField } from './form.subform.anchor';
import {
	addOwnerField,
	isValidDefaultOwnerField,
	isValidOwnerField,
} from '../../nested.list/form.nested.list.owner.util';

export class AnchorSubFormMutator {
	private field: FormModelFieldSubForm;

	constructor(field: FormModelFieldSubForm) {
		this.field = field;
	}

	private get state(): FormModelState {
		return this.field.getForm().state;
	}

	/**
	 * Set the SubForm Type of this field to ANCHOR while linking the existing lead length, owner,
	 * Creates lead length, owner fields if they don't exist.
	 */
	public enable() {
		// first try to find a field that matches the id, if that does not exist,
		// find the first accurate anchor field, otherwise select any vector field
		let leadLengthField =
			this.field.fields.find(
				(field) =>
					(field.id === PoleFieldIds.ANCHOR.LENGTH || field.id === IkePoleForemanFieldIds.ANCHOR_LEAD_LENGTH) &&
					isValidLeadLengthField(field)
			) ?? this.field.fields.find(isValidLeadLengthField);
		// first try to find a field that matches the id, if that does not exist,
		// find the select list field with default owner options, otherwise select any nested list field
		let ownerField =
			this.field.fields.find(
				(field) =>
					(field.id === PoleFieldIds.ANCHOR.OWNER || field.id === IkePoleForemanFieldIds.ANCHOR_OWNER) &&
					isValidOwnerField(field)
			) ??
			this.field.fields.find(isValidDefaultOwnerField) ??
			this.field.fields.find(isValidOwnerField);

		if (!ownerField) {
			ownerField = this.addOwnerField();
		}

		if (!leadLengthField) {
			leadLengthField = this.addLeadLengthField();
		}
		if (
			leadLengthField.type === IkeFieldType.VECTOR &&
			(leadLengthField.id === PoleFieldIds.ANCHOR.LENGTH ||
				leadLengthField.id === IkePoleForemanFieldIds.ANCHOR_LEAD_LENGTH)
		) {
			this.state.setFieldValue(this.field.id + ':' + leadLengthField.id, {
				...leadLengthField.stateValue,
				type: IkeFieldType.ANCHOR,
				locked: true,
				isGenerated: true,
			});
		}

		this.setFieldReferences(leadLengthField.id, ownerField.id);
		this.updateLockState(true);
	}

	/**
	 * Disable ANCHOR subform type, unlocking all anchor fields and clearing state.
	 */
	public disable() {
		this.updateLockState(false);

		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: SubFormType.NONE,
			anchorTypeState: undefined, // Clear to avoid stale field references
			...(!LOCKED_MODEL_POLE_FIELDS[this.field.id] && { locked: false }),
		} satisfies FormFieldSubFormState);
	}

	/**
	 * Set the SubForm Type of this field to ANCHOR.
	 *
	 * @param leadLengthFieldId The Anchor Lead Length field ID to use.
	 * @param ownerFieldId The Anchor Onwer field ID to use.
	 * @throws If any of the given field IDs do not exist in this subform.
	 */
	public setFieldReferences(leadLengthFieldId: string, ownerFieldId: string) {
		if (!this.field.hasField(leadLengthFieldId)) {
			throw new Error(`Lead Length field '${leadLengthFieldId}' not found`);
		}
		if (!this.field.hasField(ownerFieldId)) {
			throw new Error(`Owner field '${ownerFieldId}' not found`);
		}
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: SubFormType.ANCHOR,
			anchorTypeState: {
				leadLengthFieldId,
				ownerFieldId,
			},
			locked: true,
		} satisfies FormFieldSubFormState);
		this.setFieldsMetadata(ownerFieldId);
		this.updateLockState(true);
	}

	public setFieldsMetadata(ownerFieldId: string) {
		if (ownerFieldId === PoleFieldIds.ANCHOR.OWNER || ownerFieldId === IkePoleForemanFieldIds.ANCHOR_OWNER) {
			this.field.getField(ownerFieldId).Mutator.cloneable(IkeCloneable.ALL);
		}
	}

	public selectLeadLengthField(fieldId: string) {
		this.updateFieldReference('leadLengthFieldId', fieldId);
	}

	public selectOwnerField(fieldId: string) {
		this.updateFieldReference('ownerFieldId', fieldId);
	}

	private updateFieldReference(key: keyof SubFormAnchorTypeState, newFieldId: string) {
		const currentState = this.field.stateValue.anchorTypeState;

		if (!currentState) {
			return;
		}

		const oldFieldId = currentState?.[key];

		const newState: SubFormAnchorTypeState = {
			...currentState,
			[key]: newFieldId,
		};

		if (newState.leadLengthFieldId && newState.ownerFieldId && newFieldId) {
			this.state.setFieldValue(this.field.fieldId, {
				...this.field.stateValue,
				subFormType: SubFormType.ANCHOR,
				anchorTypeState: newState,
				locked: true,
			} satisfies FormFieldSubFormState);
		}

		const oldField = oldFieldId ? this.field.fields.find((f) => f.id === oldFieldId) : undefined;
		const newField = this.field.fields.find((f) => f.id === newFieldId);

		if (oldField && !LOCKED_MODEL_POLE_FIELDS[oldField.id]) {
			oldField.Mutator.setLockState(false);
		}
		if (key !== 'ownerFieldId') {
			newField?.Mutator.setLockState(true);
		}
	}

	public addLeadLengthField() {
		return this.field.createField(
			IkeFieldType.ANCHOR,
			{
				id: IkeId.generate(),
				displayName: 'Lead Length',
				locked: true,
				isGenerated: true,
			},
			0
		);
	}

	public addOwnerField(index: number = 1) {
		return addOwnerField(this.field, index);
	}

	private updateLockState(isLocked: boolean) {
		const leadLengthField = this.field.fields.find(
			(field) => field.id === this.field.subFormAnchorState?.leadLengthFieldId
		);
		const ownerField = this.field.fields.find((field) => field.id === this.field.subFormAnchorState?.ownerFieldId);

		if (isLocked) {
			leadLengthField?.Mutator.setLockState(isLocked);
			return;
		}

		// Fields with legacy-locked ids keep their lock — converted fields are not treated as generated
		if (leadLengthField && !LOCKED_MODEL_POLE_FIELDS[leadLengthField.id]) {
			leadLengthField.Mutator.setLockState(isLocked);
		}
		if (ownerField && !LOCKED_MODEL_POLE_FIELDS[ownerField.id]) {
			ownerField.Mutator.setLockState(isLocked);
		}
	}
}
